import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { 
  Card, 
  Badge, 
  Button, 
  Spinner,
  Modal,
  Input
} from '../components/ui';
import { adminApi } from '../services/api';
import { toast } from 'sonner';
import { ORDER_STATUS_MAP } from '../constants';
import { GoogleMap, useJsApiLoader, Marker } from '@react-google-maps/api';
import { useAdminSocket } from '../hooks/useAdminSocket';
import CreateRouteModal from '../components/modals/CreateRouteModal';
import Chart from 'react-apexcharts';
import { GOOGLE_MAPS_API_KEY, isGoogleMapsConfigured } from '../config/googleMaps';
import GoogleMapsSetupMessage from '../components/GoogleMapsSetupMessage';
import GoogleMapsLoadError from '../components/GoogleMapsLoadError';

const mapContainerStyle = {
  width: '100%',
  height: '100%',
  borderRadius: '2rem'
};

const mapOptions = {
  disableDefaultUI: true,
  zoomControl: true,
  styles: [
    { "featureType": "water", "elementType": "geometry", "stylers": [{ "color": "#1e293b" }] },
    { "featureType": "landscape", "elementType": "geometry", "stylers": [{ "color": "#0f172a" }] },
    { "featureType": "road", "elementType": "geometry", "stylers": [{ "color": "#1e293b" }] },
    { "featureType": "poi", "stylers": [{ "visibility": "off" }] },
    { "elementType": "labels.text.fill", "stylers": [{ "color": "#94a3b8" }] },
    { "elementType": "labels.text.stroke", "stylers": [{ "visibility": "off" }] }
  ]
};

function AdminDashboardMap({ activeRoutes }) {
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-admin-dashboard',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
  });

  if (loadError) return <GoogleMapsLoadError error={loadError} />;
  if (!isLoaded) return <Spinner />;

  return (
    <GoogleMap
      mapContainerStyle={mapContainerStyle}
      center={{ lat: 41.2995, lng: 69.2401 }}
      zoom={12}
      options={mapOptions}
    >
      {activeRoutes.map((route) => (
        <Marker
          key={route.id}
          position={{
            lat: parseFloat(route.lastLocationLat) || 41.2995,
            lng: parseFloat(route.lastLocationLng) || 69.2401,
          }}
          icon={{
            url: 'https://cdn-icons-png.flaticon.com/512/3063/3063822.png',
            scaledSize: new window.google.maps.Size(30, 30),
          }}
        />
      ))}
    </GoogleMap>
  );
}

const AdminDashboard = () => {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentOrders, setRecentOrders] = useState([]);
  const [activeRoutes, setActiveRoutes] = useState([]);
  const [analytics, setAnalytics] = useState([]);
  const [users, setUsers] = useState([]);
  const [showCreateRoute, setShowCreateRoute] = useState(false);
  const ordersPage = 1;
  const usersPage = 1;
  const ITEMS_PER_PAGE = 5; // Reduced for dashboard view

  // Balance Modal State
  const [selectedUser, setSelectedUser] = useState(null);
  const [isBalanceModalOpen, setIsBalanceModalOpen] = useState(false);
  const [newBalance, setNewBalance] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchInitialData = useCallback(async () => {
    try {
      const [statsData, ordersData, routesData, analyticsData, usersData] = await Promise.all([
        adminApi.getStats(),
        adminApi.getRecentOrders(),
        adminApi.getActiveRoutes(),
        adminApi.getAnalytics(),
        adminApi.getUsers()
      ]);
      setStats(statsData);
      setRecentOrders(ordersData || []);
      setActiveRoutes(routesData || []);
      setAnalytics(analyticsData || []);
      setUsers(usersData || []);
    } catch (err) {
      console.error("Dashboard error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleLocationUpdate = useCallback((data) => {
    setActiveRoutes((prev) =>
      prev.map((route) =>
        route.id === data.routeId
          ? { ...route, lastLocationLat: data.lat, lastLocationLng: data.lng }
          : route,
      ),
    );
  }, []);

  const handleQueueUpdate = useCallback(() => {
    fetchInitialData();
    toast.info(t('notifications.newOrder'));
  }, [fetchInitialData, t]);

  useAdminSocket({
    onLocationUpdate: handleLocationUpdate,
    onQueueUpdate: handleQueueUpdate,
  });

  useEffect(() => {
    fetchInitialData();
    const interval = setInterval(fetchInitialData, 60000);
    return () => clearInterval(interval);
  }, [fetchInitialData]);

  const handleToggleBlock = async (userId, isBlocked) => {
    try {
      await adminApi.blockUser(userId, !isBlocked);
      toast.success(isBlocked ? t('dashboard.userActivated') : t('dashboard.userBlocked'));
      fetchInitialData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleUpdateBalance = async (e) => {
    e.preventDefault();
    try {
      setIsSubmitting(true);
      await adminApi.updateUser(selectedUser.id, { balance: Number(newBalance) });
      toast.success(t('dashboard.balanceUpdated'));
      setIsBalanceModalOpen(false);
      fetchInitialData();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const openBalanceModal = (user) => {
    setSelectedUser(user);
    setNewBalance(user.balance || 0);
    setIsBalanceModalOpen(true);
  };

  const paginatedOrders = recentOrders.slice((ordersPage - 1) * ITEMS_PER_PAGE, ordersPage * ITEMS_PER_PAGE);
  const paginatedUsers = users.slice((usersPage - 1) * ITEMS_PER_PAGE, usersPage * ITEMS_PER_PAGE);

  if (loading) return <div className="flex h-[80vh] items-center justify-center"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-10">
      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-6"
      >
        <div>
          <div className="flex items-center gap-2 mb-3">
            <div className="h-1.5 w-8 bg-[#1e9f6b] rounded-full" />
            <span className="text-[10px] font-black uppercase tracking-[0.3em] text-[#1e9f6b] italic">CONTROL CENTER</span>
          </div>
          <h1 className="text-4xl font-black text-slate-900 dark:text-white italic uppercase tracking-tighter">{t('dashboard.title')}</h1>
          <p className="text-slate-500 font-bold italic uppercase text-[10px] tracking-widest mt-2 opacity-70">{t('dashboard.subtitle')}</p>
        </div>
        <div className="flex gap-4">
          <Button onClick={fetchInitialData} variant="ghost" size="sm" className="h-12 w-12 p-0 rounded-2xl hover:bg-[#1e9f6b]/10 hover:text-[#1e9f6b] transition-colors">
            <span className="material-symbols-outlined">refresh</span>
          </Button>
          <Button 
            onClick={() => setShowCreateRoute(true)}
            variant="emerald" className="h-14 px-8 rounded-3xl font-black uppercase italic text-xs tracking-widest shadow-xl shadow-emerald-500/20"
          >
            <span className="material-symbols-outlined mr-2">add_road</span>
            {t('dashboard.createRoute')}
          </Button>
        </div>
      </motion.div>

      <AnimatePresence>
        {showCreateRoute && (
          <CreateRouteModal 
            onClose={() => setShowCreateRoute(false)} 
            onSuccess={fetchInitialData}
          />
        )}
      </AnimatePresence>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard title={t('dashboard.todayOrders')} value={stats?.totalOrdersToday} icon="shopping_cart" color="blue" delay={0.1} />
        <StatCard title={t('dashboard.activeDrivers')} value={stats?.activeDrivers} icon="local_shipping" color="emerald" delay={0.2} />
        <StatCard title={t('dashboard.pendingOrders')} value={stats?.pendingOrders} icon="hourglass_empty" color="amber" delay={0.3} />
        <StatCard title={t('dashboard.todayRevenue')} value={stats?.revenueToday?.toLocaleString()} icon="payments" color="indigo" suffix={t('common.uzs')} delay={0.4} />
      </div>

      {/* Analytics Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.5 }}>
            <Card className="p-8 bg-white dark:bg-slate-900 rounded-[40px] border-slate-100 dark:border-slate-800/50 shadow-xl shadow-slate-900/5">
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-[10px] font-black text-slate-900 dark:text-white uppercase tracking-[0.3em] italic flex items-center gap-3">
                        <div className="h-8 w-8 bg-indigo-500/10 rounded-xl flex items-center justify-center text-indigo-500">
                          <span className="material-symbols-outlined text-[18px]">payments</span>
                        </div>
                        {t('dashboard.weeklyRevenue')}
                    </h3>
                    <Badge variant="slate" className="rounded-xl font-black italic uppercase text-[9px] tracking-widest">{t('dashboard.last7Days')}</Badge>
                </div>
                <div className="h-72">
                  <Chart
                    options={{
                      chart: { type: 'area', toolbar: { show: false }, background: 'transparent' },
                      stroke: { curve: 'smooth', width: 4, colors: ['#4f46e5'] },
                      fill: {
                        type: 'gradient',
                        gradient: { shadeIntensity: 1, opacityFrom: 0.45, opacityTo: 0.05, stops: [20, 100, 100, 100] }
                      },
                      xaxis: {
                        categories: analytics.map(d => d.date.split(',')[0]),
                        axisBorder: { show: false },
                        axisTicks: { show: false },
                        labels: { style: { colors: '#94a3b8', fontWeight: 900, fontFamily: 'Outfit' } }
                      },
                      yaxis: { labels: { show: false } },
                      grid: { show: false },
                      dataLabels: { enabled: false },
                      tooltip: { theme: 'dark', x: { show: true } },
                      colors: ['#4f46e5']
                    }}
                    series={[{ name: t('dashboard.todayRevenue'), data: analytics.map(d => d.revenue) }]}
                    type="area"
                    height="100%"
                  />
                </div>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.6 }}>
            <Card className="p-8 bg-white dark:bg-slate-900 rounded-[40px] border-slate-100 dark:border-slate-800/50 shadow-xl shadow-slate-900/5">
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-[10px] font-black text-slate-900 dark:text-white uppercase tracking-[0.3em] italic flex items-center gap-3">
                        <div className="h-8 w-8 bg-emerald-500/10 rounded-xl flex items-center justify-center text-emerald-500">
                          <span className="material-symbols-outlined text-[18px]">database</span>
                        </div>
                        {t('dashboard.cylindersSold')}
                    </h3>
                    <Badge variant="green" className="rounded-xl font-black italic uppercase text-[9px] tracking-widest">{t('dashboard.volume')}</Badge>
                </div>
                <div className="h-72">
                  <Chart
                    options={{
                      chart: { type: 'bar', toolbar: { show: false }, background: 'transparent' },
                      plotOptions: { bar: { borderRadius: 10, columnWidth: '40%', distributed: true } },
                      xaxis: {
                        categories: analytics.map(d => d.date.split(',')[0]),
                        axisBorder: { show: false },
                        axisTicks: { show: false },
                        labels: { style: { colors: '#94a3b8', fontWeight: 900, fontFamily: 'Outfit' } }
                      },
                      yaxis: { labels: { show: false } },
                      grid: { show: false },
                      dataLabels: { enabled: false },
                      tooltip: { theme: 'dark' },
                      legend: { show: false },
                      colors: analytics.map((_, i) => i % 2 === 0 ? '#10b981' : '#34d399')
                    }}
                    series={[{ name: t('dashboard.cylindersSold'), data: analytics.map(d => d.cylinders) }]}
                    type="bar"
                    height="100%"
                  />
                </div>
            </Card>
          </motion.div>
      </div>

      {/* Routes & Orders Section */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
         <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.7 }} className="xl:col-span-2">
            <Card className="h-[550px] p-2 relative overflow-hidden bg-white dark:bg-slate-900 rounded-[40px] border-slate-100 dark:border-slate-800/50 shadow-2xl shadow-slate-900/5">
                <div className="absolute top-6 left-6 z-20">
                  <div className="bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl p-4 rounded-3xl shadow-2xl border border-white/20 dark:border-slate-800/50">
                    <h4 className="font-black text-[8px] uppercase tracking-[0.4em] text-slate-400 mb-1 italic">{t('dashboard.liveMonitoring')}</h4>
                    <p className="text-xl font-black text-slate-900 dark:text-white italic tracking-tighter">{activeRoutes.length} <span className="text-[9px] uppercase font-black text-slate-400">TRUCKS</span></p>
                  </div>
                </div>
                <div className="w-full h-full rounded-[36px] overflow-hidden border-4 border-slate-50 dark:border-slate-800/20">
                   {!isGoogleMapsConfigured() ? (
                     <GoogleMapsSetupMessage />
                   ) : (
                     <AdminDashboardMap activeRoutes={activeRoutes} />
                   )}
                </div>
            </Card>
         </motion.div>

         <div className="flex flex-col gap-8">
            <Card className="flex-1 bg-white dark:bg-slate-900 rounded-[40px] border-slate-100 dark:border-slate-800/50 shadow-xl overflow-hidden flex flex-col">
               <div className="p-6 border-b border-slate-50 dark:border-slate-800/50 flex justify-between items-center">
                  <h3 className="text-[9px] font-black uppercase tracking-widest text-slate-900 dark:text-white italic">{t('main.recentOrders')}</h3>
                  <Button variant="ghost" size="xs" className="h-8 px-4 rounded-xl text-[9px] font-black uppercase italic tracking-widest">SEE ALL</Button>
               </div>
               <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {paginatedOrders.map(order => (
                     <div key={order.id} className="p-4 rounded-2xl bg-slate-50/50 dark:bg-slate-800/30 border border-slate-100/50 dark:border-slate-700/50 flex items-center justify-between group">
                        <div className="flex items-center gap-3">
                           <div className="h-10 w-10 bg-white dark:bg-slate-800 rounded-xl flex items-center justify-center font-black text-[10px] text-[#1e9f6b] shadow-sm italic uppercase">
                              #{order.id.slice(0, 4)}
                           </div>
                           <div>
                              <div className="text-xs font-black italic uppercase text-slate-800 dark:text-white">{order.user?.name || 'Mijoz'}</div>
                              <div className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">{order.quantity} ballon</div>
                           </div>
                        </div>
                        <Badge variant={ORDER_STATUS_MAP[order.status]?.color || 'slate'} className="rounded-xl italic uppercase text-[7px] border-0">{order.status}</Badge>
                     </div>
                  ))}
               </div>
            </Card>
         </div>
      </div>

      {/* Users Section */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1 }}>
        <Card className="overflow-hidden bg-white dark:bg-slate-900 rounded-[40px] border-slate-100 dark:border-slate-800/50 shadow-xl">
           <div className="p-8 border-b border-slate-50 dark:border-slate-800/50 flex justify-between items-center">
              <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-900 dark:text-white italic">{t('dashboard.userManagement')}</h3>
              <Badge variant="slate" className="rounded-xl font-black italic uppercase text-[8px]">{users.length} TOTAL</Badge>
           </div>
           <div className="overflow-x-auto">
             <table className="w-full text-left border-separate border-spacing-0">
               <tbody className="divide-y divide-slate-50 dark:divide-slate-800/50">
                 {paginatedUsers.map((u) => (
                   <tr key={u.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-all group">
                     <td className="px-10 py-5">
                       <div className="flex items-center gap-4">
                         <div className="h-10 w-10 bg-slate-100 dark:bg-slate-800 rounded-xl flex items-center justify-center overflow-hidden italic uppercase shadow-inner">
                           {u.avatarUrl ? <img src={u.avatarUrl} alt="" className="h-full w-full object-cover" /> : <span className="font-black text-xs text-slate-400">{u.name?.[0] || 'U'}</span>}
                         </div>
                         <div>
                           <div className="font-black text-xs italic uppercase tracking-tight text-slate-900 dark:text-white">{u.name || 'Foydalanuvchi'}</div>
                           <div className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">{u.phone}</div>
                         </div>
                       </div>
                     </td>
                     <td className="px-6 py-5">
                        <Badge variant="slate" className="rounded-xl italic uppercase text-[7px] border-0">{u.role}</Badge>
                     </td>
                     <td className="px-6 py-5">
                        <span className="text-xs font-black italic uppercase text-[#1e9f6b]">{(u.balance || 0).toLocaleString()} UZS</span>
                     </td>
                     <td className="px-10 py-5 text-right">
                        <div className="flex justify-end gap-2 opacity-100 transition-opacity">
                           <Button size="xs" variant="ghost" onClick={() => openBalanceModal(u)} className="h-9 w-9 rounded-xl p-0 hover:bg-[#1e9f6b]/10 hover:text-[#1e9f6b]"><span className="material-symbols-outlined text-[18px]">account_balance_wallet</span></Button>
                           <Button size="xs" variant="ghost" onClick={() => handleToggleBlock(u.id, u.isBlocked)} className={`h-9 w-9 rounded-xl p-0 ${u.isBlocked ? 'text-red-500' : 'text-slate-400'}`}><span className="material-symbols-outlined text-[18px]">{u.isBlocked ? 'lock_open' : 'lock'}</span></Button>
                        </div>
                     </td>
                   </tr>
                 ))}
               </tbody>
             </table>
           </div>
        </Card>
      </motion.div>

      {/* Balance Modal */}
      <Modal isOpen={isBalanceModalOpen} onClose={() => setIsBalanceModalOpen(false)} title={t('dashboard.updateBalanceTitle')}>
        <form onSubmit={handleUpdateBalance} className="space-y-6">
           <div className="p-6 rounded-2xl bg-slate-50 dark:bg-slate-800/50 mb-4">
              <div className="text-[10px] font-black text-slate-400 uppercase italic mb-1">{t('profileModal.user')}</div>
              <div className="text-lg font-black italic uppercase text-slate-900 dark:text-white">{selectedUser?.name}</div>
           </div>
           <Input
              label={t('dashboard.newBalanceAmount')}
              type="number"
              value={newBalance}
              onChange={e => setNewBalance(e.target.value)}
              required
           />
           <div className="pt-6 flex gap-4">
             <Button type="button" variant="secondary" onClick={() => setIsBalanceModalOpen(false)} className="flex-1">{t('common.cancel')}</Button>
             <Button type="submit" variant="emerald" loading={isSubmitting} className="flex-1">{t('common.update')}</Button>
           </div>
        </form>
      </Modal>
    </div>
  );
};

const StatCard = ({ title, value, icon, color, delay, suffix }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
  >
    <Card className="p-8 bg-white dark:bg-slate-900 rounded-[40px] border-slate-100 dark:border-slate-800/50 shadow-xl relative overflow-hidden group border-none">
      <div className={`absolute top-0 right-0 w-32 h-32 -mr-10 -mt-10 rounded-full bg-${color}-500/5 group-hover:scale-125 transition-transform duration-700 pointer-events-none`} />
      <div className="flex justify-between items-start mb-6 relative z-10">
        <div className={`w-14 h-14 rounded-2xl bg-${color}-500/10 flex items-center justify-center shadow-inner group-hover:scale-110 transition-transform duration-500 text-${color}-600`}>
          <span className="material-symbols-outlined text-[28px]" style={{ fontVariationSettings: "'FILL' 1" }}>{icon}</span>
        </div>
      </div>
      <div className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] italic mb-1 relative z-10">{title}</div>
      <div className="text-4xl font-black text-slate-900 dark:text-white italic tracking-tighter relative z-10">
        {value !== undefined ? (
          <>
            {value}
            {suffix && <span className="text-[10px] uppercase font-black not-italic text-slate-400 ml-2">{suffix}</span>}
          </>
        ) : <Spinner size="sm" />}
      </div>
    </Card>
  </motion.div>
);

export default AdminDashboard;
